import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form"
import cx from "classnames"
import { Button, InputText } from "src/shared/components/forms"
import { InputCurrency } from "src/shared/sections/payment/InputCurrency"
import Icon from "src/shared/icons"
import Dropdown from "src/shared/components/forms/selelct-dropdown/Dropdown"
import { Modal } from "src/shared/components/layout/modal/Modal"
import { getCurrencyFormat } from "src/utils/string"
import { PAYMENT_TYPES } from "src/constants/payments"
import { isVisibleCardNumber, validateOnlyNumbers } from "src/shared/sections/payment/Payment.helpers"
import { useModalSize } from "src/shared/sections/payment/mixto/ModalMixto.hooks"
import ModalContent from "src/shared/components/modal/sections/ModalContent/ModalContent"
import ModalBody from "src/shared/components/modal/sections/ModalBody/ModalBody"
import ModalRow from "src/shared/components/modal/sections/ModalRow/ModalRow"
import ModalFooter from "src/shared/components/modal/sections/ModalFooter/ModalFooter"
import InputTextSuggestions, { InputTextSuggestion } from "src/shared/components/forms/input-text-suggestions/InputTextSuggestions"
import { TiposPagos, useGetColaboradoresQuery, useGetEasyrewardsLazyQuery } from "src/gql/schema"
import { useProfile } from "src/shared/hooks/useProfile"
import accountBoxFill from "src/shared/icons/accountBoxFill"
import { inputIconStyle } from "src/shared/sections/payment/Payment.constants"
import {
    InternalFormValues,
    InternalItem,
    ModalPagoMixtoInputProps,
    PaymentMixtoItem,
} from "./ModalPagoMixtoInput.types"
import "./ModalPagoMixtoInput.css"
import { TipSection } from "./sections/Tip"

const EMPTY_ITEM: InternalItem = {
    method: "",
    amount: 0,
    number: "",
    easyReward: { id: "", saldo: 0 },
    colaboradorConsumoInterno: null,
    propina: {
        porcentaje: "",
        colaboradorPropina: null,
    },
}

function itemToInternal(item: PaymentMixtoItem): InternalItem {
    return {
        method: item.method,
        amount: item.amount,
        number: item.number ?? "",
        easyReward: item.easyReward ?? { id: "", saldo: 0 },
        colaboradorConsumoInterno: item.colaboradorConsumoInterno ?? null,
        propina: {
            porcentaje: item.propina?.porcentaje ?? "",
            monto: item.propina?.monto,
            colaboradorPropina: item.propina?.colaboradorPropina ?? null,
        },
    }
}

function internalToOutput(item: InternalItem): PaymentMixtoItem {
    const result: PaymentMixtoItem = {
        method: item.method as TiposPagos,
        amount: item.amount,
        number: item.number
    }

    if (item.easyReward?.id) {
        result.easyReward = item.easyReward
    }

    if (item.colaboradorConsumoInterno && typeof item.colaboradorConsumoInterno !== "string") {
        result.colaboradorConsumoInterno = item.colaboradorConsumoInterno
    }

    if (Number(item.propina?.porcentaje) > 0 || (item.propina?.monto ?? 0) > 0) {
        result.propina = {
            porcentaje: item.propina.porcentaje,
            monto: item.propina.monto,
            colaboradorPropina: item.propina.colaboradorPropina as InputTextSuggestion,
        }
    }

    return result
}

function getInitialItems(value?: PaymentMixtoItem[], defaultValue?: PaymentMixtoItem[]): InternalItem[] {
    const source = value ?? defaultValue
    if (source && source.length > 0) return source.map(itemToInternal)
    return [{ ...EMPTY_ITEM }, { ...EMPTY_ITEM }]
}

// Inline LovePoints input decoupled from external form context
interface LovePointsFieldProps {
    value: { id: string; saldo: number }
    onChange: (value: { id: string; saldo: number }) => void
    error?: boolean
}

function LovePointsField({ value, onChange, error }: LovePointsFieldProps) {
    const [search, setSearch] = useState(value?.id ?? "")
    const [getEasyrewards, { loading }] = useGetEasyrewardsLazyQuery()
    const timerRef = useRef<NodeJS.Timer>()

    useEffect(() => {
        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => {
            if (search.length >= 1 && search.length <= 5) {
                getEasyrewards({ variables: { easyrewards_id: search } }).then(({ data }) => {
                    if (data?.obten_saldo?.saldo !== undefined) {
                        onChange({ id: search.slice(0, 5), saldo: data.obten_saldo.saldo ?? 0 })
                    }
                })
            }
        }, 300)
    }, [search])

    return (
        <InputText
            loader={loading}
            icon={Icon}
            iconProps={{ name: "iconHash", ...inputIconStyle }}
            label="Número de membresía"
            type="text"
            placeholder="Máximo 5 dígitos"
            value={search}
            error={error}
            errorhinttext={error ? "Campo obligatorio" : ""}
            onChange={(e) => {
                const newValue = e.target.value
                if (validateOnlyNumbers(newValue, 5)) {
                    setSearch(newValue)
                }
            }}
        />
    )
}

function ModalPagoMixtoInput({
    visible = false,
    onClose,
    value,
    defaultValue,
    onChange,
    total,
    paymentOptions,
    withPendingPayment = false,
    validateTotal = true,
    onBack,
    onConfirm,
    isAlreadyInModal = false,
    withCancelButton = true,
    className,
    modalBodyStyle,
    title = "Método de pago mixto"
}: ModalPagoMixtoInputProps): JSX.Element {
    const { hotel_id } = useProfile()

    const { data: colaboradoresData } = useGetColaboradoresQuery({
        variables: { hotel_id },
    })

    const colaboradores = useMemo(
        () =>
            colaboradoresData?.colaboradores?.map((c) => ({
                title: `${c.nombre} ${c.apellido_paterno} ${c.apellido_materno}`,
                id: c.colaborador_id,
            })) ?? [],
        [colaboradoresData]
    )

    const { control, trigger, clearErrors, setError, reset } = useForm<InternalFormValues>({
        defaultValues: { items: getInitialItems(value, defaultValue) },
    })

    const { fields, append, remove } = useFieldArray({ control, name: "items" })
    const items = useWatch({ control, name: "items" })

    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    // Emit onChange on every internal change
    useEffect(() => {
        onChangeRef.current(items.map(internalToOutput))
    }, [items])

    // Store snapshot for cancel
    const snapshotRef = useRef<InternalItem[]>([])

    useEffect(() => {
        if (visible) {
            const initial = getInitialItems(value, defaultValue)
            reset({ items: initial })
            snapshotRef.current = initial
        }
    }, [visible])

    const modalSize = useModalSize(fields.length)

    const getAcum = (array: InternalItem[]): number =>
        array.reduce((acum, val) => acum + (val?.amount ?? 0), 0)

    const observer = useMemo(
        () => ({
            currentAcum: getAcum(items),
            beforeAcum: getAcum(items.filter((_, i) => i !== fields.length - 1)),
            occupiedSpaces: items.filter(({ amount }) => amount > 0).length,
        }),
        [items]
    )

    const payments = useMemo(() => {
        const base = [
            ...paymentOptions,
            ...(withPendingPayment ? [{ label: "Pago pendiente", value: "pendiente" as TiposPagos }] : []),
        ]
        return items.find((item) => item.method === "efectivo")
            ? base.filter(({ value }) => value !== "efectivo")
            : base
    }, [items, paymentOptions, withPendingPayment])

    const handleTypeChange = (method: string, index: number) => {
        // If switching away from LovePoints clear easyReward
        if (method !== PAYMENT_TYPES.LovePoints && items[index]?.easyReward?.id) {
            // Handled by resetting easyReward in the controller
        }
    }

    const handleBlurCardNumber = (cardValue: string, index: number) => {
        const array = [...items]
        const collect = array.filter(({ method }) => method === array[0]?.method)
        if (collect.length > 1) {
            const sameNumber = collect.filter(({ number }) => number === cardValue)
            if (sameNumber.length > 1) setError(`items.${index}.number`, { type: "value" })
        }
    }

    const handleCancel = () => {
        reset({ items: snapshotRef.current })
        onClose()
    }

    const handleConfirm = async () => {
        const valid = await trigger(["items"])

        if (!valid) return

        if (validateTotal) {
            if (total === observer.currentAcum) {
                onConfirm()
            }
        } else {
            onConfirm()
        }
    }

    const Remove = ({ index = 0 }) =>
        fields.length > 2 ? (
            <div
                className="modal-pago-mixto__field__remove"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter") remove(index)
                }}
                onClick={() => remove(index)}
            >
                <Icon height={24} width={24} name="trashFilled" color="var(--purple-drawer-primario)" />
            </div>
        ) : null

    const modalContent = () => (
        <ModalContent className={cx(isAlreadyInModal ? "modal-pago-mixto-main" : "", className)}>
            <ModalRow style={{ marginTop: "20px" }}>
                <h2 className="modal-pago-mixto__title">{title}</h2>
                <div className="modal-pago-mixto__subtitle__box">
                    <p className="modal-pago-mixto__subtitle">
                        {"Total de cuenta: "}
                        <strong>{getCurrencyFormat(total, "complete")}</strong>
                    </p>
                    <p className="modal-pago-mixto__subtitle">
                        {"Total pagado: "}
                        <strong>{getCurrencyFormat(observer.currentAcum, "complete")}</strong>
                    </p>
                    <p className="modal-pago-mixto__subtitle">
                        {"Por pagar: "}
                        <strong>{getCurrencyFormat(total - observer.currentAcum, "complete")}</strong>
                    </p>
                </div>
            </ModalRow>
            <ModalBody style={{ overflowY: "scroll", ...modalBodyStyle }}>
                {fields.map((field, index) => (
                    <Fragment key={field.id}>
                        <span className="modal-pago-mixto__label">{`Método de pago ${index + 1}`}</span>
                        <div className="modal-pago-mixto__field">
                            <div className="modal-pago-mixto__row">
                                {/* Amount */}
                                <Controller
                                    control={control}
                                    name={`items.${index}.amount`}
                                    rules={{
                                        required: true,
                                        min: validateTotal
                                            ? index !== items.length - 1
                                                ? 1
                                                : total - observer.beforeAcum
                                            : undefined,
                                        max:
                                            index !== items.length - 1
                                                ? total
                                                : total - observer.beforeAcum,
                                    }}
                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                        <InputCurrency
                                            whiteSpace={true}
                                            limit={index === 0 ? total : undefined}
                                            value={value}
                                            onChange={(v) => {
                                                onChange(v)
                                                if (error) clearErrors(`items.${index}.amount`)
                                            }}
                                            error={!!error}
                                            errorhinttext={
                                                !validateTotal && error?.type === "max"
                                                    ? `Ingresa un monto menor o igual a ${getCurrencyFormat(
                                                        total - observer.beforeAcum
                                                    )}`
                                                    : observer.currentAcum > 0
                                                        ? error?.type === "min"
                                                            ? `Ingresa un monto igual a ${getCurrencyFormat(
                                                                total - observer.beforeAcum
                                                            )}`
                                                            : error?.type === "max"
                                                                ? observer.beforeAcum >= total
                                                                    ? "Edita un monto anterior, para agregar otra cantidad"
                                                                    : `Ingresa un monto igual a ${getCurrencyFormat(
                                                                        total - observer.beforeAcum
                                                                    )}`
                                                                : "Ingresa un monto"
                                                        : "Ingresa un monto"
                                            }
                                        />
                                    )}
                                />

                                {/* Payment method */}
                                <Controller
                                    control={control}
                                    name={`items.${index}.method`}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                        <Dropdown
                                            icon="creditCard"
                                            iconInOptions={false}
                                            errorHintText={
                                                error?.type === "required" ? "Selecciona una opción" : ""
                                            }
                                            className={cx({
                                                "modal-pago-mixto__select": true,
                                                "modal-pago-mixto__select--error": !!error?.type,
                                            })}
                                            label="Forma de pago"
                                            placeholder="Selecciona una opción"
                                            options={value === "efectivo" ? paymentOptions : payments}
                                            value={value}
                                            onClick={({ value: v }) => {
                                                handleTypeChange(v, index)
                                                onChange(v)
                                                trigger(`items.${index}.method`)
                                            }}
                                        />
                                    )}
                                />

                                {/* LovePoints input */}
                                {items?.[index]?.method === PAYMENT_TYPES.LovePoints ? (
                                    <Controller
                                        control={control}
                                        name={`items.${index}.easyReward`}
                                        rules={{
                                            required: true,
                                            validate: (v) => !!v?.id,
                                        }}
                                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                                            <LovePointsField
                                                value={value}
                                                onChange={onChange}
                                                error={!!error}
                                            />
                                        )}
                                    />
                                ) : (
                                    /* Card / transfer number */
                                    isVisibleCardNumber(items?.[index]?.method) && (
                                        <Controller
                                            control={control}
                                            name={`items.${index}.number`}
                                            rules={{
                                                required: true,
                                                minLength: 4,
                                                maxLength: 10,
                                            }}
                                            render={({
                                                field: { value, onChange },
                                                fieldState: { error },
                                            }) => (
                                                <InputText
                                                    icon={Icon}
                                                    error={!!error}
                                                    errorhinttext={
                                                        error?.type === "value"
                                                            ? "Método de pago duplicado. Introduce otro."
                                                            : "Campo obligatorio"
                                                    }
                                                    iconProps={{
                                                        name: "iconHash",
                                                        ...inputIconStyle,
                                                    }}
                                                    label={
                                                        items?.[index]?.method ===
                                                            PAYMENT_TYPES.DepositoOTransferencia
                                                            ? "Número de clabe o referencia"
                                                            : "Número de tarjeta o referencia"
                                                    }
                                                    type="text"
                                                    placeholder={
                                                        items?.[index]?.method ===
                                                            PAYMENT_TYPES.DepositoOTransferencia
                                                            ? "Ingresa número"
                                                            : "Máximo 10 dígitos"
                                                    }
                                                    value={value}
                                                    onChange={(e) => {
                                                        const v = e.target.value
                                                        if (
                                                            validateOnlyNumbers(v, 10) ||
                                                            items?.[index]?.method ===
                                                            PAYMENT_TYPES.DepositoOTransferencia
                                                        ) {
                                                            onChange(v)
                                                            if (error) clearErrors(`items.${index}.number`)
                                                        }
                                                    }}
                                                    onBlur={(e) =>
                                                        handleBlurCardNumber(e.target.value, index)
                                                    }
                                                />
                                            )}
                                        />
                                    )
                                )}

                                {/* ConsumoInterno: colaborador input */}
                                {items?.[index]?.method === PAYMENT_TYPES.ConsumoInterno && (
                                    <Controller
                                        control={control}
                                        name={`items.${index}.colaboradorConsumoInterno`}
                                        rules={{
                                            required: true,
                                            validate: (v) =>
                                                typeof v !== "string" && !!v?.id && !!v?.title,
                                        }}
                                        render={({
                                            field: { value, onChange },
                                            fieldState: { error },
                                        }) => (
                                            <InputTextSuggestions
                                                suggestionsListWidth="255px"
                                                suggestions={colaboradores}
                                                value={value ?? ""}
                                                inputTextConfig={{
                                                    label: "Nombre del personal",
                                                    placeholder: "Nombre del personal",
                                                    type: "text",
                                                    icon: accountBoxFill,
                                                    error: !!error,
                                                    errorhinttext:
                                                        error?.type === "validate" ||
                                                            error?.type === "required"
                                                            ? "Escribe el nombre del responsable"
                                                            : "",
                                                }}
                                                onChange={(e) => {
                                                    clearErrors(`items.${index}.colaboradorConsumoInterno`)
                                                    onChange(e)
                                                }}
                                            />
                                        )}
                                    />
                                )}

                                <Remove index={index} />
                            </div>
                        </div>

                        {/* Propina section */}
                        {items?.[index]?.amount > 0 &&
                            items?.[index]?.method &&
                            items?.[index]?.method !== PAYMENT_TYPES.LovePoints &&
                            items?.[index]?.method !== PAYMENT_TYPES.ConsumoInterno &&
                            items?.[index]?.method !== PAYMENT_TYPES.Cortesia ? (
                            <div className="modal-pago-mixto__tip-section">
                                <p className="modal-pago-mixto__tip__title">
                                    {"Incluir propina (opcional)"}
                                </p>
                                <div className="modal-pago-mixto__tip__row">
                                    <TipSection control={control} amount={items?.[index]?.amount || 0} index={index} />
                                </div>

                                {/* si se selecciona un porcentaje mostrar el input de colaborador, solo para el ultimo field */}
                                {(Number(items?.[index]?.propina?.porcentaje) > 0 || (items?.[index]?.propina?.monto ?? 0) > 0) && (index === fields.length - 1) && (
                                    <div className="modal-pago-mixto__tip__colab">
                                        <Controller
                                            control={control}
                                            name={`items.${index}.propina.colaboradorPropina`}
                                            rules={{
                                                required:
                                                    !!items?.[index]?.propina?.monto ||
                                                    !!items?.[index]?.propina?.porcentaje,
                                                validate: (v) =>
                                                    (!items?.[index]?.propina?.monto &&
                                                        !items?.[index]?.propina?.porcentaje) ||
                                                    (typeof v !== "string" && !!v?.id && !!v?.title),
                                            }}
                                            render={({
                                                field: { value, onChange },
                                                fieldState: { error },
                                            }) => (
                                                <InputTextSuggestions
                                                    suggestionsListWidth="255px"
                                                    suggestions={colaboradores}
                                                    value={value ?? ""}
                                                    inputTextConfig={{
                                                        label: "¿Quién recibió la propina?",
                                                        placeholder: "Nombre del personal",
                                                        type: "text",
                                                        icon: accountBoxFill,
                                                        error: !!error,
                                                        errorhinttext:
                                                            error?.type === "validate" ||
                                                                error?.type === "required"
                                                                ? "Escribe el nombre del responsable"
                                                                : "",
                                                    }}
                                                    onChange={(e) => {
                                                        clearErrors(`items.${index}.propina.colaboradorPropina`)
                                                        onChange(e)
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </Fragment>
                ))}

                {fields.length <= 3 && observer.currentAcum < total ? (
                    <div
                        tabIndex={0}
                        className="modal-pago-mixto__link"
                        onClick={() => {
                            append({ ...EMPTY_ITEM })
                            clearErrors("items")
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                append({ ...EMPTY_ITEM })
                                clearErrors("items")
                            }
                        }}
                    >
                        <Icon name="plusCircle" color="var(--purple-drawer-primario)" />
                        <span>{"Agrega método de pago"}</span>
                    </div>
                ) : null}
            </ModalBody>

            <ModalFooter>
                <div className="modal-pago-mixto__footer">
                    {withCancelButton && (
                        <Button
                            onClick={handleCancel}
                            type="button"
                            className="modal-pago-mixto__button"
                            text="Cancelar"
                            theme="secondary"
                        />
                    )}
                    <Button
                        onClick={handleConfirm}
                        type="button"
                        className="modal-pago-mixto__button"
                        text="Registrar pago"
                    />
                </div>
            </ModalFooter>
        </ModalContent>
    )

    return isAlreadyInModal ? (
        modalContent()
    ) : (
        <Modal
            className={cx(
                "modal-pago-mixto",
                `modal-pago-mixto__height__${modalSize}`,
                className ?? ""
            )}
            isOpen={visible}
            height={600}
            onClose={handleCancel}
            isCancelableOnClickOutside={false}
            withCloseButton
            withBackButton={!!onBack}
            onBack={() => onBack?.()}
        >
            {modalContent()}
        </Modal>
    )
}

export default ModalPagoMixtoInput
